/**
 * SplicingPreviewModal Component
 *
 * Displays a timeline preview when activating a bounded record (one with effectiveTo date).
 * Shows what will happen to existing records and allows the user to choose whether to
 * create continuation records to maintain timeline coverage.
 *
 * Key features:
 * - Enhanced Timeline component with pan/zoom and dual BEFORE/AFTER view
 * - RecordsComparisonTable showing all affected records
 * - Collapsible field changes panel
 * - LINEAGE-BASED color system (records in same family share colors):
 *   - Focus (blue): NEW record being activated
 *   - Family 0-11: Original records and their derivatives (splices, continuations)
 *     share the same palette color from the 12-color visualization palette
 *   - Overwritten records: Family color with strikethrough text
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Modal, Space, Typography, Table, Tag, Tooltip, Switch } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  FieldTimeOutlined,
  RightOutlined,
  DownOutlined,
  CloseOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import type {
  SplicingPreviewData,
  SplicingText,
  SupersessionRecord,
  SpliceRecord,
  ContinuationRecord,
} from '../data/types'
import type { FieldMetadata } from '@/services/metadataService'
import { isJsonValue, formatJsonPreview } from '@/components/MetadataTable/utils/jsonFormatters'
import { ComparisonView } from '@/components/ComparisonView'
import { TimelineContainer, previewDataToTimelineData, mergeFullHistoryIntoTimeline } from '@/components/Timeline'
import type { TimelineSegmentType } from '@/components/Timeline'
import { dataService } from '@/services/dataService'
import './SplicingPreviewModal.css'

const { Text } = Typography

// Layout constants
const MODAL_WIDTH = 900
const MAX_KEY_FIELD_COLUMNS = 3

// localStorage key for timeline expanded state preference
const TIMELINE_EXPANDED_KEY = 'rp-splicing-timeline-expanded'

/**
 * Record row data structure for the comparison table.
 *
 * Family-based coloring: Records from the same lineage share a color.
 * - familyIndex: -1 = focus color (NEW record), 0-11 = family palette colors
 * - Original records and their derivatives (splices, continuations) share the same family index
 */
interface RecordRow {
  uid: string
  type: 'superseded' | 'new' | 'overwritten' | 'continuation' | 'unaffected'
  /** Family index for lineage-based coloring: -1 = focus (NEW), 0-11 = family colors, undefined = unaffected (gray) */
  familyIndex: number | undefined
  name: string
  effectiveFrom: string | null
  effectiveTo: string | null
  /** Actual half-open interval boundary for effectiveTo (for debugging tooltips) */
  actualEffectiveTo?: string | null
  /** Original effectiveTo before truncation (for superseded records - struck-through display) */
  originalEffectiveTo?: string | null
  /** Original effectiveFrom before shift (for continuation records - shows source record's start) */
  originalEffectiveFrom?: string | null
  record: Record<string, unknown>
  isSelectable: boolean
  /** Whether this is an unaffected record (not involved in current splice - can be past or future) */
  isUnaffected?: boolean
}

/**
 * Full history record shape from API
 */
interface FullHistoryRecord {
  uid: string
  name: string
  effectiveFrom: string | null
  effectiveTo: string | null
  [key: string]: unknown
}

/**
 * Format a date for display, handling null as "No End Date" or "(ongoing)".
 */
function formatDate(date: string | null | undefined, forEnd = false): string {
  if (!date) return forEnd ? '(ongoing)' : 'Immediate'
  return dayjs(date).format('MMM D, YYYY')
}

/**
 * Get CSS color variable for a family index.
 * Returns the CSS variable string for use in inline styles.
 * - familyIndex -1 = focus (blue) = --rp-palette-focus
 * - familyIndex 0-11 = family colors = --rp-palette-{n}
 * - undefined = null (no color bar)
 */
function getFamilyColor(familyIndex: number | undefined): string | undefined {
  if (familyIndex === undefined) return undefined
  if (familyIndex === -1) return 'var(--rp-palette-focus)'
  return `var(--rp-palette-${familyIndex})`
}

/**
 * Format a date with full timestamp precision for debugging.
 * Shows ISO format with milliseconds in UTC to help diagnose alignment issues.
 * Uses UTC to match backend storage and avoid timezone confusion.
 */
function formatFullTimestamp(date: string | null | undefined): string {
  if (!date) return '(null)'
  return dayjs(date).utc().format('YYYY-MM-DD HH:mm:ss.SSS') + ' UTC'
}

export interface SplicingPreviewModalProps {
  /** Whether the modal is visible */
  visible: boolean
  /** Preview data from the API */
  previewData: SplicingPreviewData | null
  /** Splicing text labels from API metadata */
  splicingText: SplicingText | null
  /** Whether the action is in progress */
  loading?: boolean
  /** Field metadata for diff display (optional - diff panel only shows if provided) */
  fieldMetadata?: Record<string, FieldMetadata>
  /** Key fields to highlight in diff panel and show as columns (from comparison.key_fields in schema) */
  keyFields?: string[]
  /** Field to use for timeline segment labels (from comparison.timeline_label_field), or null to disable labels */
  timelineLabelField?: string | null
  /**
   * Whether to prefix "NEW" to the focus segment label in the timeline.
   * - true: Label becomes "NEW" (if no label) or "NEW - {label}"
   * - false/undefined: Use regular label logic
   *
   * This is independent from timelineLabelField - even if labels are disabled,
   * showNewPrefix will show "NEW" on the focus segment.
   */
  showNewPrefix?: boolean
  /** Singular entity name for modal title (e.g., "Offering") */
  entityLabel?: string
  /** Explicit fields to show in title (from display.title_fields) */
  titleFields?: string[]
  /** Endpoint for fetching full history (e.g., 'offerings') - enables "Show all versions" toggle */
  historyEndpoint?: string
  /** Key combo hash for filtering full history - required if historyEndpoint is provided */
  keyComboHash?: string
  /** Callback when user confirms the activation */
  onConfirm: (createContinuations: boolean) => void
  /** Callback when user cancels */
  onCancel: () => void
}

/**
 * SplicingPreviewModal Component
 */
export function SplicingPreviewModal({
  visible,
  previewData,
  splicingText,
  loading = false,
  fieldMetadata,
  keyFields = [],
  timelineLabelField,
  showNewPrefix,
  entityLabel,
  titleFields,
  historyEndpoint,
  keyComboHash,
  onConfirm,
  onCancel,
}: SplicingPreviewModalProps) {
  // Continuations are always created automatically
  const createContinuations = true

  // State for selected supersession (for diff comparison)
  const [selectedSupersessionUid, setSelectedSupersessionUid] = useState<string | null>(null)

  // State for hovered record (for bidirectional highlighting)
  const [hoveredUid, setHoveredUid] = useState<string | null>(null)

  // Throttled hover handler to prevent render thrashing (50ms)
  const lastHoverCallRef = useRef<number>(0)
  const pendingHoverRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const THROTTLE_MS = 50

  const handleHover = useCallback((uid: string | null) => {
    const now = Date.now()
    const timeSinceLastCall = now - lastHoverCallRef.current

    // Clear any pending throttled call
    if (pendingHoverRef.current) {
      clearTimeout(pendingHoverRef.current)
      pendingHoverRef.current = null
    }

    if (timeSinceLastCall >= THROTTLE_MS) {
      // Enough time has passed, execute immediately
      lastHoverCallRef.current = now
      setHoveredUid(uid)
    } else {
      // Schedule execution after remaining throttle time
      pendingHoverRef.current = setTimeout(() => {
        lastHoverCallRef.current = Date.now()
        setHoveredUid(uid)
        pendingHoverRef.current = null
      }, THROTTLE_MS - timeSinceLastCall)
    }
  }, [])

  // Cleanup throttle timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingHoverRef.current) {
        clearTimeout(pendingHoverRef.current)
      }
    }
  }, [])

  // State for diff panel expansion
  const [diffExpanded, setDiffExpanded] = useState(false)

  // State for showing all versions (unaffected records toggle)
  const [showAllVersions, setShowAllVersions] = useState(false)

  // Helper to read timeline expanded preference from localStorage
  const readTimelinePreference = useCallback((): boolean => {
    try {
      return localStorage.getItem(TIMELINE_EXPANDED_KEY) === 'true'
    } catch {
      return false
    }
  }, [])

  // State for timeline expanded (persisted to localStorage, default: collapsed)
  // Initialize synchronously from localStorage to avoid flash
  const [timelineExpanded, setTimelineExpanded] = useState(readTimelinePreference)

  // Re-read from localStorage when modal becomes visible
  // This handles the case where the component stays mounted between row changes
  useEffect(() => {
    if (visible) {
      setTimelineExpanded(readTimelinePreference())
    }
  }, [visible, readTimelinePreference])

  // Handler for user-initiated toggle - writes to localStorage immediately
  const handleTimelineExpandedChange = useCallback((newValue: boolean) => {
    setTimelineExpanded(newValue)
    try {
      localStorage.setItem(TIMELINE_EXPANDED_KEY, String(newValue))
    } catch {
      // Silently fail - preference won't persist but UI still works
    }
  }, [])

  // State for tracking if selected row is visible (for scroll-to-selection indicator)
  const [isSelectedRowVisible, setIsSelectedRowVisible] = useState(true)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Memoize derived values
  const { newRecord, supersessions, splices, continuations } = useMemo(() => {
    if (!previewData) {
      return {
        newRecord: null,
        supersessions: [] as SupersessionRecord[],
        splices: [] as SpliceRecord[],
        continuations: [] as ContinuationRecord[],
      }
    }
    return {
      newRecord: previewData.newRecord,
      supersessions: previewData.supersessions || [],
      splices: previewData.splices || [],
      continuations: previewData.continuations || [],
    }
  }, [previewData])

  // Fetch full history when toggle is ON
  const { data: fullHistoryData } = useQuery({
    queryKey: ['full-history', historyEndpoint, keyComboHash],
    queryFn: async () => {
      if (!historyEndpoint || !keyComboHash) return []
      const response = await dataService.search<FullHistoryRecord>(historyEndpoint, {
        filters: [
          { key: 'keyComboHash', constraint_type: 'EQUAL', value: keyComboHash },
          { key: 'lifecycleState', constraint_type: 'EQUAL', value: 'active' },
          { key: 'deletedAt', constraint_type: 'IS_NULL' },
        ],
        order_by: [{ key: 'effectiveFrom', direction: 'asc' }],
        limit: 100,
      })
      return response.success ? response.data || [] : []
    },
    enabled: showAllVersions && visible && !!historyEndpoint && !!keyComboHash,
  })

  // Build record rows for the comparison table (must be before auto-select effect)
  const recordRows = useMemo((): RecordRow[] => {
    if (!newRecord) return []

    const rows: RecordRow[] = []

    // Build family map: supersession uid -> color index (0-11, cycling)
    // Each original record and its derivatives share the same family color
    const familyMap = new Map<string, number>()
    supersessions.forEach((s, index) => {
      familyMap.set(s.uid, index % 12) // Cycle through 12 palette colors
    })

    // Track which supersessions have been processed via splices
    const processedSupersessionUids = new Set<string>()

    // Add PRE-splice rows (records that start BEFORE NEW)
    // These come directly from the backend's splices array - no derivation needed
    // PRE-splices are TRUNCATED records - they still exist in the AFTER timeline,
    // just with a shorter duration. They should NEVER be marked as 'overwritten'.
    for (const splice of splices) {
      if (splice.type !== 'pre') continue

      processedSupersessionUids.add(splice.sourceUid)

      // Find the source supersession record for comparison data
      const sourceRecord = supersessions.find((s) => s.uid === splice.sourceUid)

      // PRE-splice records are always 'superseded' (truncated but still present).
      // 'overwritten' is only for records COMPLETELY removed from the timeline.
      const rowType = 'superseded'

      // Use the actual effectiveTo boundary - no adjustment needed
      // Both effectiveTo and the next record's effectiveFrom are the same instant,
      // so they should display as the same date to show contiguous coverage
      const displayEffectiveTo = splice.effectiveTo

      // Get family index from source record
      const familyIndex = familyMap.get(splice.sourceUid) ?? 0

      rows.push({
        uid: splice.sourceUid,
        type: rowType,
        familyIndex,
        name: splice.name,
        effectiveFrom: splice.effectiveFrom,
        effectiveTo: displayEffectiveTo,
        // Actual half-open interval boundary (for debugging tooltips)
        actualEffectiveTo: splice.effectiveTo,
        // Show original effectiveTo in strikethrough (from source record)
        originalEffectiveTo: sourceRecord?.effectiveTo ?? null,
        record: sourceRecord?.record ?? { name: splice.name },
        isSelectable: true,
      })
    }

    // Add COMPLETELY OVERWRITTEN records (supersessions not represented in splices
    // AND that don't have a continuation). If a record has a continuation, the
    // continuation row already shows all necessary info (original dates struck through).
    const continuationSourceUids = new Set(
      continuations.filter((c) => c.willCreate).map((c) => c.sourceUid)
    )

    for (const supersession of supersessions) {
      // Skip if already processed via PRE-splice
      if (processedSupersessionUids.has(supersession.uid)) continue

      // Skip if this record has a continuation - the continuation row shows all info
      if (continuationSourceUids.has(supersession.uid)) continue

      // Get family index for this supersession
      const familyIndex = familyMap.get(supersession.uid) ?? 0

      // This record is completely overwritten - no splice was created for it
      rows.push({
        uid: supersession.uid,
        type: 'overwritten',
        familyIndex,
        name: (supersession.record.name as string) || 'Unnamed',
        effectiveFrom: supersession.effectiveFrom,
        effectiveTo: supersession.effectiveTo,
        // No date change - record is completely replaced
        originalEffectiveTo: null,
        record: supersession.record,
        isSelectable: true,
      })
    }

    // Add the NEW record - always uses focus color (familyIndex = -1)
    // NEW is selectable for educational purposes (shows empty state in diff panel)
    rows.push({
      uid: newRecord.uid,
      type: 'new',
      familyIndex: -1, // Focus color for NEW record
      name: newRecord.name,
      effectiveFrom: newRecord.effectiveFrom,
      effectiveTo: newRecord.effectiveTo,
      record: newRecord as Record<string, unknown>,
      isSelectable: true, // Selectable to show educational empty state
    })

    // Add continuation records (POST-splices) - these are selectable for comparison
    // Continuations inherit their parent's family color
    for (const cont of continuations) {
      if (cont.willCreate) {
        // Find the source record to get its original effectiveFrom
        const sourceRecord = supersessions.find((s) => s.uid === cont.sourceUid)

        // Inherit family index from source record
        const familyIndex = familyMap.get(cont.sourceUid) ?? 0

        rows.push({
          uid: `continuation-${cont.sourceUid}`,
          type: 'continuation',
          familyIndex,
          name: (cont.preview?.name as string) || 'Resumed',
          effectiveFrom: cont.effectiveFrom,
          effectiveTo: cont.effectiveTo,
          // Store source record's effectiveFrom so we can show it was "interrupted"
          originalEffectiveFrom: sourceRecord?.effectiveFrom ?? null,
          record: cont.preview || {},
          isSelectable: true, // Continuations are selectable for comparison
        })
      }
    }

    // Add unaffected records from full history (if enabled and data available)
    if (showAllVersions && fullHistoryData && fullHistoryData.length > 0) {
      // Get UIDs of all affected records (already added above)
      const affectedUids = new Set<string>()
      for (const row of rows) {
        // Handle continuation UIDs
        if (row.uid.startsWith('continuation-')) {
          affectedUids.add(row.uid.replace('continuation-', ''))
        } else {
          affectedUids.add(row.uid)
        }
      }

      // Add unaffected records (past or future versions not involved in splice)
      for (const record of fullHistoryData) {
        if (affectedUids.has(record.uid)) continue

        rows.push({
          uid: record.uid,
          type: 'unaffected',
          familyIndex: undefined, // Gray for unaffected
          name: record.name || 'Unnamed',
          effectiveFrom: record.effectiveFrom,
          effectiveTo: record.effectiveTo,
          record: record as Record<string, unknown>,
          isSelectable: true,
          isUnaffected: true,
        })
      }
    }

    // Sort by effectiveFrom date
    // NULL effectiveFrom means "effective immediately" - use NOW for sorting
    // This matches the backend semantic convention
    const nowTimestamp = Date.now()
    rows.sort((a, b) => {
      const aFrom = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : nowTimestamp
      const bFrom = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : nowTimestamp
      return aFrom - bFrom
    })

    return rows
  }, [newRecord, supersessions, splices, continuations, showAllVersions, fullHistoryData])

  // Filter out overwritten records when timeline is collapsed (showing only "Revised" view)
  const displayRows = useMemo(() => {
    if (timelineExpanded) return recordRows
    return recordRows.filter(r => r.type !== 'overwritten')
  }, [recordRows, timelineExpanded])

  const hiddenOverwrittenCount = recordRows.filter(r => r.type === 'overwritten').length

  // Auto-select the NEW record when modal opens
  // This shows the educational empty state in the diff panel by default
  useEffect(() => {
    if (!selectedSupersessionUid && newRecord) {
      setSelectedSupersessionUid(newRecord.uid)
    }
  }, [newRecord, selectedSupersessionUid])

  // Reset selection when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedSupersessionUid(null)
      setDiffExpanded(false)
      setShowAllVersions(false)
      setIsSelectedRowVisible(true)
    }
  }, [visible])

  // Observe selected row visibility for scroll-to-selection indicator
  useEffect(() => {
    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    if (!selectedSupersessionUid || !visible) {
      setIsSelectedRowVisible(true)
      return
    }

    // Find the selected row element
    const selectedRow = tableContainerRef.current?.querySelector(
      `.splicing-row-selected`
    ) as HTMLElement | null

    if (!selectedRow) {
      setIsSelectedRowVisible(true)
      return
    }

    // Get the scrollable container (Ant Table's scroll body)
    const scrollContainer = tableContainerRef.current?.querySelector(
      '.ant-table-body'
    ) as HTMLElement | null

    if (!scrollContainer) {
      setIsSelectedRowVisible(true)
      return
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) {
          setIsSelectedRowVisible(entry.isIntersecting)
        }
      },
      {
        root: scrollContainer,
        threshold: 0.1, // Row is considered visible if at least 10% is visible
      }
    )

    observerRef.current.observe(selectedRow)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [selectedSupersessionUid, visible, recordRows])

  // Apply hover class directly to DOM to bypass Ant Design Table's memoization
  // This ensures bidirectional hover highlighting works reliably
  useEffect(() => {
    if (!tableContainerRef.current || !visible) return

    // Remove hover class from all rows first
    const allRows = tableContainerRef.current.querySelectorAll('.ant-table-row')
    allRows.forEach((row) => row.classList.remove('splicing-row-hover'))

    if (!hoveredUid) return

    // Find the row(s) that should be highlighted
    // Check for direct UID match and continuation variants
    const rowsToHighlight: Element[] = []

    allRows.forEach((row) => {
      const rowKey = row.getAttribute('data-row-key')
      if (!rowKey) return

      const isMatch =
        rowKey === hoveredUid ||
        (hoveredUid.startsWith('continuation-') && rowKey === hoveredUid.replace('continuation-', '')) ||
        (rowKey.startsWith('continuation-') && hoveredUid === rowKey.replace('continuation-', ''))

      if (isMatch) {
        rowsToHighlight.push(row)
      }
    })

    // Add hover class to matching rows
    rowsToHighlight.forEach((row) => {
      // Don't add hover class if row is already selected
      if (!row.classList.contains('splicing-row-selected')) {
        row.classList.add('splicing-row-hover')
      }
    })
  }, [hoveredUid, visible, recordRows])

  // Check if the NEW row is currently selected (for educational empty state)
  const isNewRowSelected = useMemo(() => {
    return selectedSupersessionUid === newRecord?.uid
  }, [selectedSupersessionUid, newRecord?.uid])

  // Get the currently selected record for comparison
  // Can be a superseded record (from supersessions array), a continuation record, or an unaffected record (from recordRows)
  const selectedRecord = useMemo(() => {
    if (!selectedSupersessionUid) return null

    // NEW row doesn't have a comparison record
    if (newRecord && selectedSupersessionUid === newRecord.uid) {
      return null
    }

    // Check if it's a continuation or unaffected record (look in recordRows first)
    const row = recordRows.find((r) => r.uid === selectedSupersessionUid)
    if (row && (row.type === 'continuation' || row.type === 'unaffected')) {
      return { record: row.record, name: row.name }
    }

    // Otherwise look in supersessions array
    const supersession = supersessions.find((s) => s.uid === selectedSupersessionUid)
    return supersession ? { record: supersession.record, name: (supersession.record.name as string) || 'Selected' } : null
  }, [selectedSupersessionUid, supersessions, recordRows, newRecord])

  // Get the selected row's family color for ComparisonView theming
  const selectedRowColors = useMemo(() => {
    if (!selectedSupersessionUid) {
      return { left: undefined, right: getFamilyColor(-1) } // Default to focus blue for right
    }

    // Find the selected row to get its familyIndex
    const row = recordRows.find((r) => r.uid === selectedSupersessionUid)
    const leftColor = row ? getFamilyColor(row.familyIndex) : undefined
    const rightColor = getFamilyColor(-1) // Always focus blue for NEW record

    return { left: leftColor, right: rightColor }
  }, [selectedSupersessionUid, recordRows])

  // Use default text if not provided
  const text: SplicingText = splicingText || {
    modalTitle: 'Timeline Preview: Schedule New Version',
    modalSubtitle: 'You are scheduling a new version that will affect the timeline for:',
    newVersionLabel: 'New Version',
    currentVersionLabel: 'Current Active Version',
    continuationLabel: 'Resumed Version',
    continuationOptionLabel: 'Resume previous configuration after this version ends (recommended)',
    continuationDescription: 'A resumed version will maintain the original configuration from {date} onward',
    noContinuationOptionLabel: 'End without continuation',
    noContinuationWarning: 'This {entity} will have no active configuration after {date}',
    gapWarningTitle: 'Coverage Gap',
    gapWarningBullet1: 'No active version will exist after {date}',
    gapWarningBullet2: 'No active settings will exist for this {entity} after that date',
    confirmButtonLabel: 'Apply Changes',
  }

  // Handle confirmation
  const handleConfirm = useCallback(() => {
    onConfirm(createContinuations)
  }, [onConfirm, createContinuations])

  // Scroll to selected row (for scroll-to-selection indicator)
  const scrollToSelectedRow = useCallback(() => {
    const selectedRow = tableContainerRef.current?.querySelector(
      `.splicing-row-selected`
    ) as HTMLElement | null

    if (selectedRow) {
      selectedRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [])

  // Get primary field values for display columns
  const getPrimaryFieldValue = useCallback((record: Record<string, unknown>, field: string): string => {
    const value = record[field]
    if (value === null || value === undefined) return '-'
    if (typeof value === 'number') {
      // Format as currency if it looks like price
      if (field.toLowerCase().includes('price') || field.toLowerCase().includes('cost')) {
        return `$${value.toFixed(2)}`
      }
      return String(value)
    }
    // JSON objects/arrays get compact preview
    if (isJsonValue(value)) {
      return formatJsonPreview(value)
    }
    return String(value)
  }, [])

  // Build table columns
  const columns = useMemo((): ColumnsType<RecordRow> => {
    const baseColumns: ColumnsType<RecordRow> = [
      // 1. Color Bar column (16px) - full saturation vertical indicator
      {
        title: '',
        key: 'colorBar',
        width: 16,
        className: 'splicing-color-bar-cell',
        render: (_, record) => {
          let barClass: string
          if (record.type === 'unaffected' || record.familyIndex === undefined) {
            barClass = 'splicing-color-bar-unaffected'
          } else if (record.familyIndex === -1) {
            barClass = 'splicing-color-bar-focus'
          } else {
            barClass = `splicing-color-bar-family-${record.familyIndex}`
          }
          return <div className={`splicing-color-bar ${barClass}`} />
        },
      },
    ]

    // 3. Add keyField columns (up to 3) - ALL treated generically
    const primaryFields = keyFields.slice(0, MAX_KEY_FIELD_COLUMNS)
    for (const field of primaryFields) {
      const fieldMeta = fieldMetadata?.[field]
      const isNameField = field === 'name'

      baseColumns.push({
        title: fieldMeta?.title || field.charAt(0).toUpperCase() + field.slice(1),
        key: field,
        width: isNameField ? undefined : 100, // Name gets flex, others fixed
        ellipsis: true,
        render: (_, record) => {
          const value = isNameField ? record.name : getPrimaryFieldValue(record.record, field)
          // Add NEW tag only for name field on new records
          if (isNameField && record.type === 'new') {
            return <>{value} <Tag color="blue" style={{ marginLeft: 8 }}>NEW</Tag></>
          }
          return value
        },
      })
    }

    // 4. Effective From (140px) - slightly narrower to fit more columns
    baseColumns.push({
      title: 'Effective From',
      key: 'effectiveFrom',
      width: 140,
      render: (_, record) => {
        // For continuations, show original start date struck through with new resume date
        if (record.type === 'continuation' && record.originalEffectiveFrom && record.originalEffectiveFrom !== record.effectiveFrom) {
          return (
            <span className="splicing-date-changed">
              <Tooltip title={formatFullTimestamp(record.originalEffectiveFrom)} mouseLeaveDelay={0}>
                <span className="date-strikethrough">{formatDate(record.originalEffectiveFrom)}</span>
              </Tooltip>
              {' '}
              <Tooltip title={formatFullTimestamp(record.effectiveFrom)} mouseLeaveDelay={0}>
                <span className="date-new">{formatDate(record.effectiveFrom)}</span>
              </Tooltip>
            </span>
          )
        }
        return (
          <Tooltip title={formatFullTimestamp(record.effectiveFrom)} mouseLeaveDelay={0}>
            <span>{formatDate(record.effectiveFrom)}</span>
          </Tooltip>
        )
      },
    })

    // 5. Effective To (140px)
    baseColumns.push({
      title: 'Effective To',
      key: 'effectiveTo',
      width: 140,
      render: (_, record) => {
        // Use actualEffectiveTo for tooltip (half-open interval boundary) if available
        const tooltipDate = record.actualEffectiveTo ?? record.effectiveTo

        // If there's an original date that differs from current, show strikethrough
        if (record.originalEffectiveTo && record.originalEffectiveTo !== record.effectiveTo) {
          return (
            <span className="splicing-date-changed">
              <Tooltip title={formatFullTimestamp(record.originalEffectiveTo)} mouseLeaveDelay={0}>
                <span className="date-strikethrough">{formatDate(record.originalEffectiveTo, true)}</span>
              </Tooltip>
              {' '}
              <Tooltip title={`Display: ${formatFullTimestamp(record.effectiveTo)} | Actual boundary: ${formatFullTimestamp(tooltipDate)}`} mouseLeaveDelay={0}>
                <span className="date-new">{formatDate(record.effectiveTo, true)}</span>
              </Tooltip>
            </span>
          )
        }
        return (
          <Tooltip title={formatFullTimestamp(tooltipDate)} mouseLeaveDelay={0}>
            <span>{formatDate(record.effectiveTo, true)}</span>
          </Tooltip>
        )
      },
    })

    return baseColumns
  }, [keyFields, fieldMetadata, selectedSupersessionUid, getPrimaryFieldValue])

  // Row class name based on family (lineage-based coloring) and selection state
  // familyIndex: -1 = focus (NEW record), 0-11 = family palette colors, undefined = unaffected (gray)
  // NOTE: Hover class is applied via DOM manipulation (useEffect) to bypass Ant Table memoization
  const getRowClassName = useCallback((record: RecordRow): string => {
    const classes: string[] = []

    // Family-based coloring
    if (record.type === 'unaffected' || record.familyIndex === undefined) {
      classes.push('splicing-row-unaffected')
    } else if (record.familyIndex === -1) {
      classes.push('splicing-row-focus')
    } else {
      classes.push(`splicing-row-family-${record.familyIndex}`)
    }

    // Add overwritten class for strikethrough text styling
    if (record.type === 'overwritten') {
      classes.push('splicing-row-overwritten')
    }

    // Add selection state class
    if (selectedSupersessionUid === record.uid) {
      classes.push('splicing-row-selected')
    }

    return classes.join(' ')
  }, [selectedSupersessionUid])

  // Transform preview data into timeline format for the new Timeline component
  // NOTE: This useMemo must be called BEFORE the early return (React Rules of Hooks)
  const timelineData = useMemo(() => {
    if (!previewData) return null
    const baseData = previewDataToTimelineData(previewData, {
      labelField: timelineLabelField,
      showNewPrefix,
    })

    // Merge full history if enabled and data available
    if (showAllVersions && fullHistoryData && fullHistoryData.length > 0) {
      return mergeFullHistoryIntoTimeline(baseData, fullHistoryData)
    }

    return baseData
  }, [previewData, timelineLabelField, showNewPrefix, showAllVersions, fullHistoryData])

  // Helper to select a row and scroll to it
  const selectAndScrollToRow = useCallback((uid: string) => {
    setSelectedSupersessionUid(uid)

    // Use requestAnimationFrame to ensure the selection class is applied before scrolling
    requestAnimationFrame(() => {
      const selectedRow = tableContainerRef.current?.querySelector(
        `[data-row-key="${uid}"]`
      ) as HTMLElement | null

      if (selectedRow) {
        selectedRow.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    })
  }, [])

  // Handle timeline segment click - map to the original selection format
  // Handles UID mismatch between BEFORE timeline (uses supersession.uid)
  // and continuation rows (use 'continuation-${sourceUid}')
  // Also handles unaffected (background) segments
  const handleTimelineSegmentClick = useCallback((segment: TimelineSegmentType) => {
    if (segment.isSelectable !== false) {
      // Find matching recordRow - check exact UID first (works for unaffected segments too)
      let matchingRow = recordRows.find(r => r.uid === segment.uid && r.isSelectable)
      if (!matchingRow) {
        // Try continuation-prefixed version (for BEFORE segments that map to continuation rows)
        matchingRow = recordRows.find(r => r.uid === `continuation-${segment.uid}` && r.isSelectable)
      }

      if (matchingRow) {
        selectAndScrollToRow(matchingRow.uid)
      }
    }
  }, [recordRows, selectAndScrollToRow])

  // Build modal title from titleFields (explicit) or fall back to name
  // IMPORTANT: Filters out null/blank values so " + " only appears between actual values
  const modalTitle = useMemo(() => {
    const entityName = entityLabel || 'Version'

    if (!newRecord) {
      return `Schedule New ${entityName}`
    }

    // Use explicit titleFields if provided, otherwise fall back to name
    const fieldsToShow = titleFields && titleFields.length > 0
      ? titleFields
      : (newRecord.name ? ['name'] : [])

    // Collect only non-null, non-empty values
    const displayParts: string[] = []
    for (const field of fieldsToShow) {
      const value = field === 'name'
        ? newRecord.name
        : (newRecord as Record<string, unknown>)[field]

      // Skip null, undefined, empty string, and whitespace-only
      if (value === null || value === undefined) continue
      const stringValue = String(value).trim()
      if (stringValue === '') continue

      displayParts.push(stringValue)
    }

    // Only join parts that have values - no dangling " + "
    if (displayParts.length === 0) {
      return `Schedule New ${entityName}`
    }

    return `Schedule New ${entityName} - ${displayParts.join(' + ')}`
  }, [entityLabel, newRecord, titleFields])

  // Early return AFTER all hooks have been called
  if (!previewData || !newRecord) {
    return null
  }

  return (
    <Modal
      open={visible}
      wrapClassName="splicing-preview-modal"
      closable={false}
      title={
        <div className="splicing-modal-header">
          <div className="splicing-modal-header-left">
            <FieldTimeOutlined style={{ color: 'var(--rp-gray-700)', fontSize: 20 }} />
            <span className="splicing-modal-title">{modalTitle}</span>
          </div>
          <div className="splicing-modal-header-right">
            {historyEndpoint && keyComboHash && (
              <Tooltip title="Include unaffected past and future versions" mouseLeaveDelay={0}>
                <div className="splicing-modal-toggle">
                  <Text type="secondary" style={{ fontSize: 13 }}>Show all</Text>
                  <Switch
                    checked={showAllVersions}
                    onChange={setShowAllVersions}
                    size="small"
                  />
                </div>
              </Tooltip>
            )}
            <button
              type="button"
              className="splicing-modal-close"
              onClick={onCancel}
              aria-label="Close"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
      }
      onCancel={onCancel}
      onOk={handleConfirm}
      okText={text.confirmButtonLabel}
      okButtonProps={{ loading }}
      cancelButtonProps={{ disabled: loading }}
      width={MODAL_WIDTH}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>

        {/* Enhanced Timeline Visualization with dual BEFORE/AFTER view */}
        {timelineData && (
          <TimelineContainer
            data={timelineData}
            displayMode="toggle"
            expanded={timelineExpanded}
            onExpandedChange={handleTimelineExpandedChange}
            timelineHeight={40}
            selectedSegmentUid={selectedSupersessionUid}
            hoveredSegmentUid={hoveredUid}
            onSegmentClick={handleTimelineSegmentClick}
            onSegmentHover={handleHover}
            showControls={true}
            showBackgroundSegments={showAllVersions}
            animationDuration={250}
            collapsedStatusMessage={
              hiddenOverwrittenCount > 0
                ? `${hiddenOverwrittenCount} replaced ${hiddenOverwrittenCount === 1 ? 'record' : 'records'} hidden`
                : undefined
            }
          />
        )}

        {/* Records Comparison Table with scroll-to-selection indicator */}
        <div ref={tableContainerRef} className="splicing-table-container">
          <Table<RecordRow>
            className="splicing-comparison-table splicing-preview-table"
            dataSource={displayRows}
            columns={columns}
            rowKey="uid"
            rowClassName={getRowClassName}
            size="small"
            pagination={false}
            scroll={{ y: 250 }}
            onRow={(record) => ({
              onClick: () => {
                if (record.isSelectable) {
                  // Use direct setSelectedSupersessionUid for table clicks (already visible)
                  // Only auto-scroll when clicking timeline segments
                  setSelectedSupersessionUid(record.uid)
                }
              },
              onMouseEnter: () => handleHover(record.uid),
              onMouseLeave: () => handleHover(null),
              'aria-selected': selectedSupersessionUid === record.uid,
            })}
          />
          {/* Scroll-to-selection indicator */}
          {selectedSupersessionUid && !isSelectedRowVisible && (
            <button
              type="button"
              className="scroll-to-selection-button"
              onClick={scrollToSelectedRow}
              aria-label="Scroll to selected row"
            >
              <ArrowDownOutlined /> Selection
            </button>
          )}
        </div>

        {/* Collapsible Field Changes Panel */}
        {fieldMetadata && (selectedRecord || isNewRowSelected) && (
          <>
            <div
              className={`splicing-diff-toggle ${diffExpanded ? 'expanded' : ''}`}
              onClick={() => setDiffExpanded(!diffExpanded)}
            >
              {diffExpanded ? (
                <DownOutlined className="toggle-icon" />
              ) : (
                <RightOutlined className="toggle-icon" />
              )}
              <Text strong>
                {diffExpanded ? 'Hide field changes' : 'Show field changes'}
              </Text>
            </div>
            {diffExpanded && (
              <div className="splicing-diff-content">
                <ComparisonView
                  leftRecord={isNewRowSelected ? null : selectedRecord?.record ?? null}
                  rightRecord={newRecord as Record<string, unknown>}
                  mode="directed"
                  fields={fieldMetadata}
                  leftColumnColor={selectedRowColors.left}
                  rightColumnColor={selectedRowColors.right}
                  maxHeight={300}
                  collapsible={false}
                  showDifferencesOnly={true}
                />
              </div>
            )}
          </>
        )}
      </Space>
    </Modal>
  )
}

export default SplicingPreviewModal
